import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CContainer,
  CRow,
  CCol,
  CCard,
  CCardBody,
  CForm,
  CInputGroup,
  CFormInput,
  CButton,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilReload } from "@coreui/icons";
import { useCaptcha } from "../../hooks/useCaptcha";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../components/LanguageSwitcher/LanguageSwitcher";

function LoginPage({ setIsAuthenticated }) {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { loadingCaptcha, canvasRef, fetchCaptcha, countdown } = useCaptcha();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await fetch("/api/token/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password, captcha }),
      });

      const data = await response.json();
      if (response.ok) {
        localStorage.setItem("token", data.access);
        localStorage.setItem("refreshToken", data.refresh);
        localStorage.setItem("is_staff", data.is_staff);
        localStorage.setItem("is_superuser", data.is_superuser);
        localStorage.setItem("can_do_markup", data.can_do_markup);

        if (data.organization) {
          localStorage.setItem("organizationName", data.organization.name);
          localStorage.setItem("organizationId", data.organization.id);
          localStorage.setItem("is_suspended", data.organization.is_suspended);
          localStorage.setItem("org_data", JSON.stringify(data.organization));
        } else {
          localStorage.removeItem("organizationName");
          localStorage.removeItem("organizationId");
        }

        setIsAuthenticated(true);

        navigate("/dashboard");
      } else {
        toast.error(data.detail || t("login.error_auth"));
        fetchCaptcha();
        setCaptcha("");
      }
    } catch (error) {
      toast.error(t("common.error_network"));
    } finally {
      setLoading(false);
    }
  };

  const minutes = Math.floor(countdown / 60);
  const seconds = String(countdown % 60).padStart(2, "0");

  return (
    <div className="bg-light min-vh-100 d-flex flex-row align-items-center">
      <CContainer>
        <CRow className="justify-content-center">
          <CCol md={6}>
            <CCard className="p-4">
              <CCardBody>
                <div className="d-flex justify-content-end align-items-center mb-3">
                  <LanguageSwitcher />
                </div>

                <CForm onSubmit={handleSubmit}>
                  <h1>{t("login.title")}</h1>
                  <p className="text-medium-emphasis">{t("login.subtitle")}</p>
                  <CInputGroup className="mb-3">
                    <CFormInput
                      placeholder={t("login.username")}
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                    />
                  </CInputGroup>
                  <CInputGroup className="mb-3">
                    <CFormInput
                      type="password"
                      placeholder={t("login.password")}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </CInputGroup>
                  <div className="mb-4">
                    <CRow className="align-items-center mb-2">
                      <CCol>
                        <label className="form-label">
                          {t("login.captcha_label")}
                        </label>
                      </CCol>
                      <CCol className="text-end">
                        {countdown > 0 ? (
                          <small className="text-muted">
                            {t("login.expires_in")}: {minutes}:{seconds}
                          </small>
                        ) : (
                          !loadingCaptcha && (
                            <small className="text-danger">
                              {t("login.captcha_expired")}
                            </small>
                          )
                        )}
                      </CCol>
                    </CRow>
                    <CRow className="align-items-center">
                      <CCol xs="auto">
                        <div
                          style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            height: "62px",
                            width: "152px",
                            background: "#f0f0f0",
                            position: "relative",
                          }}
                        >
                          {loadingCaptcha && (
                            <div className="d-flex justify-content-center align-items-center h-100">
                              <CSpinner size="sm" />
                            </div>
                          )}
                          <canvas
                            ref={canvasRef}
                            style={{
                              display: loadingCaptcha ? "none" : "block",
                            }}
                          />
                        </div>
                      </CCol>
                      <CCol xs="auto">
                        <CButton
                          color="light"
                          onClick={() => fetchCaptcha(false)}
                          title={t("login.update_image")}
                          disabled={loadingCaptcha}
                        >
                          <CIcon icon={cilReload} />
                        </CButton>
                      </CCol>
                    </CRow>
                    <CFormInput
                      className="mt-2"
                      placeholder={t("login.captcha_placeholder")}
                      value={captcha}
                      onChange={(e) => setCaptcha(e.target.value)}
                      required
                      autoComplete="off"
                    />
                  </div>
                  <CRow>
                    <CCol xs={6}>
                      <CButton
                        color="primary"
                        className="px-4"
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? <CSpinner size="sm" /> : t("login.submit")}
                      </CButton>
                    </CCol>
                  </CRow>
                </CForm>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  );
}
export default LoginPage;